import { create } from 'zustand'

import { DropDownFilterState } from '../components/filterBottomSheet/dropDownFilterBottomSheet'
import {
  SubFilterState,
  SubFilterValuesState,
} from '../components/filterBottomSheet/subFiltersBottomSheet'

const LIFE_TIME_FREE = 'LIFE_TIME_FREE'
const RUPAY = 'RUPAY'

function flattenObject(obj: Record<string, string | Array<string> | object>) {
  const flattened = {}

  function flattenHelper(obj: Record<string, any>, prefix = '') {
    for (const key in obj) {
      if (
        Array.isArray(obj[key]) &&
        obj[key]?.every((item) => typeof item === 'string')
      ) {
        flattened[key] = obj[key].join(',')
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        flattenHelper(obj[key], key)
      } else {
        flattened[key] = obj[key]
      }
    }
  }

  flattenHelper(obj)
  return flattened
}

function checkIfFilterApplied(filters: Record<string, any>) {
  for (const key in filters) {
    if (Array.isArray(filters[key])) {
      if (filters[key].length > 0) {
        return true
      } else {
        continue
      }
    } else if (filters[key]) {
      return true
    }
  }
  return false
}

function transformFilters(filters: Record<string, any>, isMostPopular = false) {
  let transformedFilters = { ...filters }
  if (isMostPopular) {
    transformedFilters = {
      ...transformedFilters,
      showVariants: false,
      selectedFiltersTransformed: {},
      sort: '',
      subFilters: {},
      quickFilter: ['MOST_POPULAR'],
    }
  } else {
    transformedFilters = {
      ...transformedFilters,
      quickFilter: transformedFilters?.quickFilter?.filter(
        (quickFilter: string) => quickFilter !== 'MOST_POPULAR'
      ),
    }
  }

  if (
    transformedFilters?.sort === 'POPULARITY' ||
    checkIfFilterApplied(transformedFilters?.subFilters) ||
    transformedFilters?.quickFilter?.length > 0
  ) {
    transformedFilters.showVariants = true
    transformedFilters.limit = 1000
  } else {
    transformedFilters.showVariants = false
    delete transformedFilters.limit
  }
  return transformedFilters
}

type FilterStoreState = {
  initialCategoryId?: string | number
  categoryId?: string | number
  setCategoryId: (categoryId: number | string) => void
  setInitialCategoryId: (categoryId: number | string) => void

  showVariants: boolean
  selectedFiltersTransformed: Record<string, string | number | Array<string>>

  sort: DropDownFilterState
  setSort: (filters: DropDownFilterState) => void

  localSubFilters: SubFilterState
  setLocalSubFilter: (
    filters: SubFilterState | ((prevState: SubFilterState) => SubFilterState)
  ) => void

  subFilters: SubFilterState
  setSubFilter: (filters: SubFilterState) => void

  quickFilter: Array<string>
  setQuickFilter: (quickFilter: Array<string>, isMostPopular: boolean) => void

  resetSubFilterToPrevState: () => void

  setFiltersFromParam: (filters: {
    subFilters?: SubFilterState
    quickFilter?: Array<string>
    sort?: DropDownFilterState
    categoryId?: string | number
    showVariants?: boolean
    initialCategoryId?: string | number
  }) => void

  clearAllFilters: (params?: {
    resetInitialCategory?: boolean
    maintainCategoryId?: boolean
  }) => void
  setSelectedFilterValues: (
    filters: SubFilterState | ((prevState: SubFilterState) => SubFilterState)
  ) => void
  filterValues: SubFilterValuesState
}

const useFilterStore = create<FilterStoreState>((set) => ({
  showVariants: false,

  categoryId: undefined,
  initialCategoryId: undefined,

  setCategoryId: (categoryId) =>
    set((state) => {
      const selectedFiltersTransformed = {
        categoryId,
      }

      const resetAppliedFilter = {
        showVariants: false,
        sort: '',
        subFilters: {},
        quickFilter: [],
      }

      return { ...resetAppliedFilter, categoryId, selectedFiltersTransformed }
    }),

  setInitialCategoryId: (categoryId) =>
    set({
      initialCategoryId: categoryId,
    }),

  selectedFiltersTransformed: {},

  sort: '',
  setSort: (filters) =>
    set((state) => {
      const cumulativeFilters = transformFilters({
        sort: filters,
        subFilters: state.subFilters,
        quickFilter: state?.quickFilter,
        categoryId: state?.categoryId,
      })

      return {
        ...cumulativeFilters,
        selectedFiltersTransformed: flattenObject(cumulativeFilters),
      }
    }),

  localSubFilters: {},
  setLocalSubFilter: (filters) =>
    set((state) => {
      let _filters

      if (typeof filters === 'function') {
        _filters = filters?.(state?.localSubFilters)
      } else {
        _filters = filters
      }

      return {
        localSubFilters: _filters,
      }
    }),

  subFilters: {},
  setSubFilter: (filters) =>
    set((state) => {
      const subFiltersBenefitSet = filters?.benefits
      const quickFiltersSet = new Set(state?.quickFilter || [])

      if (subFiltersBenefitSet?.includes(LIFE_TIME_FREE)) {
        quickFiltersSet.add(LIFE_TIME_FREE)
      } else {
        quickFiltersSet.delete(LIFE_TIME_FREE)
      }

      if (subFiltersBenefitSet?.includes(RUPAY)) {
        quickFiltersSet.add(RUPAY)
      } else {
        quickFiltersSet.delete(RUPAY)
      }

      // Clean up empty benefits array
      const cleanedFilters = { ...filters }
      if (cleanedFilters?.benefits && cleanedFilters.benefits.length === 0) {
        delete cleanedFilters.benefits
      }

      const cumulativeFilters = transformFilters({
        sort: state?.sort,
        subFilters: cleanedFilters,
        quickFilter: Array.from(quickFiltersSet),
        categoryId: state?.categoryId,
      })
      return {
        ...cumulativeFilters,
        selectedFiltersTransformed: flattenObject(cumulativeFilters),
      }
    }),

  quickFilter: [],
  setQuickFilter: (_quickFilters, isMostPopular) =>
    set((state) => {
      const subFiltersBenefitSet = new Set(state?.subFilters?.benefits || [])

      if (_quickFilters?.includes(LIFE_TIME_FREE)) {
        subFiltersBenefitSet.add(LIFE_TIME_FREE)
      } else {
        subFiltersBenefitSet.delete(LIFE_TIME_FREE)
      }

      if (_quickFilters?.includes(RUPAY)) {
        subFiltersBenefitSet.add(RUPAY)
      } else {
        subFiltersBenefitSet.delete(RUPAY)
      }

      const _subFilters = {
        ...state?.subFilters,
      }

      if (subFiltersBenefitSet.size > 0) {
        _subFilters.benefits = Array.from(subFiltersBenefitSet)
      } else {
        delete _subFilters.benefits
      }

      const cumulativeFilters = transformFilters(
        {
          sort: state?.sort,
          subFilters: _subFilters,
          quickFilter: _quickFilters,
          categoryId: state?.categoryId,
        },
        isMostPopular
      )

      return {
        ...cumulativeFilters,
        localSubFilters: _subFilters,
        selectedFiltersTransformed: flattenObject(cumulativeFilters),
      }
    }),

  resetSubFilterToPrevState: () =>
    set((state) => ({
      localSubFilters: state?.subFilters,
    })),

  clearAllFilters: (params) =>
    set((state) => {
      const { resetInitialCategory = false, maintainCategoryId } = params || {}

      const cumulativeFilters = transformFilters({
        categoryId: undefined,
        showVariants: false,
        selectedFiltersTransformed: {},
        sort: '',
        subFilters: {},
        quickFilter: [],
        ...(resetInitialCategory && { initialCategoryId: undefined }),
        ...(maintainCategoryId && { categoryId: state?.categoryId }),
      })

      return {
        ...cumulativeFilters,
        localSubFilters: {},
        selectedFiltersTransformed: flattenObject(cumulativeFilters),
      }
    }),

  setFiltersFromParam: (filters) =>
    set(() => {
      let subFilters
      try {
        subFilters = JSON.parse(filters?.subFilters)
      } catch (error) {
        console.log('Error: Unable to parse subfilters from params (handled)')
      }

      const subFiltersBenefitSet = new Set(subFilters?.benefits || [])

      const quickFiltersSet = new Set(filters?.quickFilter || [])

      if (subFiltersBenefitSet?.has(LIFE_TIME_FREE)) {
        quickFiltersSet.add(LIFE_TIME_FREE)
      } else {
        quickFiltersSet.delete(LIFE_TIME_FREE)
      }

      if (subFiltersBenefitSet?.has(RUPAY)) {
        quickFiltersSet.add(RUPAY)
      } else {
        quickFiltersSet.delete(RUPAY)
      }

      const _subFilters = {
        ...subFilters,
      }
      
      if (subFiltersBenefitSet.size > 0) {
        _subFilters.benefits = Array.from(subFiltersBenefitSet)
      } else {
        delete _subFilters.benefits
      }

      const fetchedFilters = {
        ...(filters?.categoryId && { categoryId: filters?.categoryId }),
        ...(filters?.sort && {
          sort: filters?.sort,
        }),
        ...(!!subFilters && {
          subFilters: _subFilters,
        }),
        ...(quickFiltersSet?.size > 0 && {
          quickFilter: Array.from(quickFiltersSet),
        }),
        ...(filters?.showVariants && { showVariants: filters?.showVariants }),
        ...(filters?.initialCategoryId && {
          initialCategoryId: filters?.initialCategoryId,
        }),
      }

      return {
        ...fetchedFilters,
        localSubFilters: _subFilters,
        ...(!!subFilters && {
          localSubFilters: _subFilters,
        }),
        selectedFiltersTransformed: flattenObject(fetchedFilters),
      }
    }),
  setSelectedFilterValues: (filters) =>
    set((state) => {
      let _filters

      if (typeof filters === 'function') {
        _filters = filters?.(state?.filterValues)
      } else {
        _filters = filters
      }

      return {
        filterValues: _filters,
      }
    }),
  filterValues: {},
}))

export default useFilterStore
